// Phase 5.3 — Create/Edit Vendor screen, migrated to shadcn/Tailwind.
//
// UI only: MUI Grid + antd Form/Input/TextArea/Select/Divider → shadcn
// FormSection/FieldRow Cards + Input/Select + a plain textarea + Separator
// headings. Every dispatch / useSelector / navigate / payload is unchanged — the
// handlers below are the legacy ones (createOrUpdateVendorAction /
// getReferenceDataAction / getVendorEmailValidationAction). The antd Form is
// dropped (it never varied the payload — inputs already mirror
// `createOrUpdateVendorInputs`), so `form.setFieldsValue` is removed and the antd
// Form.Item validators (name required; email pattern; mobile pattern) are ported
// one-to-one into a local `validate()` gate; the server email-exists check
// (`emailValidation == 1`) still blocks submit. `message.warning` → sonner toast.
import { useEffect, useState } from "react"
import ApparelMenu from "./SubComponents/apparelMenu"
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { createOrUpdateVendorAction, getReferenceDataAction, getVendorEmailValidationAction } from "../Store/actions/apparelAction";
import { deepCopyFunction } from "../Utils/helpers";
import { FormSection, FieldRow } from "../shared/form-section";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import { Separator } from "ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";

const CreateVendor = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const editData = useLocation()?.state?.data;
    const isEdit = useLocation()?.state?.isEdit;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const vendorTemp = {
        vendorId: "",
        name: null,
        description: null,
        emailId: null,
        mobileNumber: null,
        websiteUrl: null,
        contactName: null,
        streetArea: null,
        city: null,
        zipcode: null,
        stateRefId: null,
        isActive: 1,
        isDeleted: 0
    }
    const [createOrUpdateVendorInputs, setCreateOrUpdateVendorInputs] = useState(deepCopyFunction(vendorTemp));
    const [createOrUpdateVendorLoad, setCreateOrUpdateVendorLoad] = useState(false);
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { createOrUpdateVendorOnLoad, getVendorEmailValidationData, getVendorEmailValidationOnLoad, stateReferenceData } = apparelReducerState;
    const stateRefList = stateReferenceData?.getReferenceData;
    const [emailValidationLoad, setEmailValidationLoad] = useState(false);
    const [emailValidation, setEmailValidation] = useState<any>(null);
    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        if (isEdit == 1) {
            setCreateOrUpdateVendorInputs({
                ...createOrUpdateVendorInputs,
                vendorId: editData?.vendorId,
                name: editData?.vendorName,
                description: editData?.description,
                emailId: editData?.emailId,
                mobileNumber: editData?.mobileNumber,
                websiteUrl: editData?.websiteUrl,
                contactName: editData?.contactName,
                streetArea: editData?.streetArea,
                city: editData?.city,
                zipcode: editData?.zipcode,
                stateRefId: editData?.stateRefId,
                isActive: editData?.isActive,
                isDeleted: 0
            })
        }
    }, [isEdit])

    const getReferenceDataApi = (ref: any) => {
        let payload: any = {
            referenceGroupId: ref
        }
        dispatch(getReferenceDataAction(payload));
    }

    useEffect(() => {
        getReferenceDataApi(7);
    }, [])

    const getVendorEmailValidationApi = () => {
        let payload = {
            vendorId: createOrUpdateVendorInputs?.vendorId ? createOrUpdateVendorInputs?.vendorId : "",
            emailId: createOrUpdateVendorInputs?.emailId
        }
        dispatch(getVendorEmailValidationAction(payload));
        setEmailValidationLoad(true);
    }

    const createOrUpdateVendorApi = () => {
        try {
            let payload = {
                vendorId: createOrUpdateVendorInputs?.vendorId,
                name: createOrUpdateVendorInputs?.name,
                description: createOrUpdateVendorInputs?.description,
                emailId: createOrUpdateVendorInputs?.emailId,
                mobileNumber: createOrUpdateVendorInputs?.mobileNumber ? createOrUpdateVendorInputs?.mobileNumber.trim() : "",
                websiteUrl: createOrUpdateVendorInputs?.websiteUrl,
                contactName: createOrUpdateVendorInputs?.contactName,
                streetArea: createOrUpdateVendorInputs?.streetArea,
                city: createOrUpdateVendorInputs?.city,
                zipcode: createOrUpdateVendorInputs?.zipcode,
                stateRefId: createOrUpdateVendorInputs?.stateRefId,
                isActive: createOrUpdateVendorInputs?.isActive,
                isDeleted: createOrUpdateVendorInputs?.isDeleted,
            }
            dispatch(createOrUpdateVendorAction(payload));
            setCreateOrUpdateVendorLoad(true);
        } catch (error) {
            console.log("error in createOrUpdateVendorApi", error);
        }
    }

    useEffect(() => {
        if (emailValidationLoad && !getVendorEmailValidationOnLoad) {
            if (getVendorEmailValidationData?.getVenderEmailValidation?.isEmail == 1) {
                toast.warning(getVendorEmailValidationData?.getVenderEmailValidation?.message);
            };
            setEmailValidation(getVendorEmailValidationData?.getVenderEmailValidation?.isEmail)
            setEmailValidationLoad(false)
        }
    }, [emailValidationLoad, getVendorEmailValidationOnLoad])

    useEffect(() => {
        if (createOrUpdateVendorLoad && !createOrUpdateVendorOnLoad) {
            toast.success(isEdit == 1 ? "Vendor updated successfully" : "Vendor added successfully");
            navigate("/vendor")
            setCreateOrUpdateVendorLoad(false);
        }
    }, [createOrUpdateVendorLoad, createOrUpdateVendorOnLoad])

    const onEmailHandle = (value: any) => {
        setCreateOrUpdateVendorInputs({ ...createOrUpdateVendorInputs, emailId: value });
        setEmailValidation(0)
    };

    useEffect(() => {
        let isMailPatternMatched = createOrUpdateVendorInputs?.emailId?.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/);
        isMailPatternMatched && getVendorEmailValidationApi()
    }, [createOrUpdateVendorInputs?.emailId])

    // antd Form.Item rules ported one-to-one.
    const validate = (): boolean => {
        const next: any = {};
        const name = createOrUpdateVendorInputs?.name;
        if (name === undefined || name === null || String(name).trim() === "") next.name = "Name is required";

        const email = createOrUpdateVendorInputs?.emailId;
        const sanitizedEmail = email && String(email).trim().toLowerCase();
        if (sanitizedEmail && !emailPattern.test(sanitizedEmail)) next.email = "Enter the valid Email";

        const mobile = createOrUpdateVendorInputs?.mobileNumber;
        const sanitizedMobile = mobile && String(mobile).trim().toLowerCase();
        if (sanitizedMobile && !/^[\d\s\+\-]{1,15}$/g.test(sanitizedMobile)) next.mobile = "Enter the valid Mobile Number";

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        if (emailValidation == 1) return; // server says email already exists
        createOrUpdateVendorApi();
    };

    const update = (patch: any) => setCreateOrUpdateVendorInputs({ ...createOrUpdateVendorInputs, ...patch });

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="6" />
            <div className="mb-4 flex items-center gap-2">
                <Button variant="ghost" size="icon" aria-label="Go back" onClick={() => navigate("/vendor")}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl font-semibold text-foreground">Create Vendor</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <FormSection columns={3}>
                    <FieldRow label="Vendor Name" required error={errors.name}>
                        <Input
                            placeholder="Enter Vendor Name"
                            value={createOrUpdateVendorInputs?.name ?? ""}
                            onChange={(e) => { update({ name: e.target.value }); setErrors((er: any) => ({ ...er, name: undefined })); }}
                        />
                    </FieldRow>
                    <FieldRow label="Email" error={errors.email}>
                        <Input
                            type="email"
                            placeholder="Enter Email"
                            value={createOrUpdateVendorInputs?.emailId ?? ""}
                            onChange={(e) => { onEmailHandle(e.target.value); setErrors((er: any) => ({ ...er, email: undefined })); }}
                        />
                    </FieldRow>
                    <FieldRow label="Mobile Number" error={errors.mobile}>
                        <Input
                            placeholder="Enter Mobile Number"
                            value={createOrUpdateVendorInputs?.mobileNumber ?? ""}
                            onChange={(e) => { update({ mobileNumber: e.target.value }); setErrors((er: any) => ({ ...er, mobile: undefined })); }}
                        />
                    </FieldRow>
                    <FieldRow label="Website">
                        <Input
                            placeholder="Enter Website"
                            value={createOrUpdateVendorInputs?.websiteUrl ?? ""}
                            onChange={(e) => update({ websiteUrl: e.target.value })}
                        />
                    </FieldRow>
                    <FieldRow label="Contact Name">
                        <Input
                            placeholder="Enter Contact Name"
                            value={createOrUpdateVendorInputs?.contactName ?? ""}
                            onChange={(e) => update({ contactName: e.target.value })}
                        />
                    </FieldRow>
                </FormSection>

                <FormSection title="Address" columns={3}>
                    <FieldRow label="Street/Area">
                        <Input
                            placeholder="Enter Street/Area"
                            value={createOrUpdateVendorInputs?.streetArea ?? ""}
                            onChange={(e) => update({ streetArea: e.target.value })}
                        />
                    </FieldRow>
                    <FieldRow label="City">
                        <Input
                            placeholder="Enter City"
                            value={createOrUpdateVendorInputs?.city ?? ""}
                            onChange={(e) => update({ city: e.target.value })}
                        />
                    </FieldRow>
                    <FieldRow label="Zipcode">
                        <Input
                            placeholder="Enter ZipCode"
                            value={createOrUpdateVendorInputs?.zipcode ?? ""}
                            onChange={(e) => update({ zipcode: e.target.value })}
                        />
                    </FieldRow>
                    <FieldRow label="State">
                        <Select
                            value={createOrUpdateVendorInputs?.stateRefId != null ? String(createOrUpdateVendorInputs?.stateRefId) : undefined}
                            onValueChange={(v) => update({ stateRefId: Number(v) })}
                        >
                            <SelectTrigger><SelectValue placeholder="Select State" /></SelectTrigger>
                            <SelectContent>
                                {(stateRefList || []).map((item: any, index: any) => (
                                    <SelectItem key={index} value={String(item?.id)}>{item?.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FieldRow>
                </FormSection>

                <FormSection title="Description" columns={1}>
                    <FieldRow label="Description" full>
                        <textarea
                            placeholder="Enter Description"
                            value={createOrUpdateVendorInputs?.description ?? ""}
                            onChange={(e) => update({ description: e.target.value })}
                            className="flex min-h-[96px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        />
                    </FieldRow>
                </FormSection>

                <Separator />
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => navigate("/vendor")}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </div>
            </form>
        </div>
    )
}

export default CreateVendor;
